import React, { useState } from 'react';

import { type Shown, formatExact, formatValue, memberLabel } from '../format';
import { columnGroups } from '../tableExtras';
import type { Block, Envelope, EnvelopeColumn, EnvelopeRow } from '../types';

type PivotTableProps = { block?: Block; envelope: Envelope; across: EnvelopeColumn; shown: Shown; totalLabel?: string };

export const acrossColumn = (envelope: Envelope, name: string) => envelope.columns.find((column) => column.role === 'dim' && (column.name === name || name.endsWith(`.${column.name}`) || column.name.endsWith(`.${name}`)));

const MOST_MEMBERS = 12;
const ROWS_PER_PAGE = 25;
const TOTAL = 'Total';

export default function PivotTable({ block, envelope, across, shown, totalLabel = 'All' }: PivotTableProps) {
  const [page, setPage] = useState(0);
  const [acrossPage, setAcrossPage] = useState(0);
  const dims = envelope.columns.filter((column) => column.role === 'dim');
  const measures = envelope.columns.filter((column) => column.role === 'measure');
  const down = dims.filter((dim) => dim.name !== across.name);
  const depth = dims.length;
  const leaves = envelope.rows.filter((row) => (row.level ?? depth) === depth);
  const rowTotals = new Map(envelope.rows.filter((row) => row.level === depth - 1).map((row) => [down.map((dim) => String(row[dim.name] ?? '')).join('›'), row]));
  const keyOf = (row: EnvelopeRow) => down.map((dim) => String(row[dim.name] ?? '')).join('›');
  const weight = new Map<string, number>();
  for (const row of leaves) weight.set(String(row[across.name]), (weight.get(String(row[across.name])) ?? 0) + Math.abs(Number(row[measures[0].name] ?? 0)));
  const allMembers = [...weight].sort((a, b) => b[1] - a[1]).map(([member]) => member);
  const rows = [...new Map(leaves.map((row) => [keyOf(row), row])).values()];
  const printing = typeof document !== 'undefined' && Boolean(document.getElementById('lens-print'));
  const lastAcrossPage = Math.max(0, Math.ceil(allMembers.length / MOST_MEMBERS) - 1);
  const currentAcrossPage = Math.min(acrossPage, lastAcrossPage);
  const shownAcrossPage = printing ? 0 : currentAcrossPage;
  const members = allMembers.slice(shownAcrossPage * MOST_MEMBERS, (shownAcrossPage + 1) * MOST_MEMBERS);
  const lastPage = Math.max(0, Math.ceil(rows.length / ROWS_PER_PAGE) - 1);
  const currentPage = Math.min(page, lastPage);
  const visibleRows = printing ? rows : rows.slice(currentPage * ROWS_PER_PAGE, (currentPage + 1) * ROWS_PER_PAGE);
  const cell = new Map(leaves.map((row) => [`${keyOf(row)}»${String(row[across.name])}`, row]));
  const groups = columnGroups(block);
  const spans = measures.reduce<Array<{ label: string; span: number }>>((out, measure) => {
    const label = groups.find((group) => group.columns.includes(measure.name))?.label ?? '';
    const last = out[out.length - 1];
    if (last && last.label === label) last.span += 1;
    else out.push({ label, span: 1 });
    return out;
  }, []);
  const many = measures.length > 1 || groups.length > 0;
  const totalled = rowTotals.size > 0;
  const ends = totalled ? [TOTAL] : [];
  const value = (row: EnvelopeRow | undefined, measure: EnvelopeColumn, ref: string) => {
    const number = row?.[measure.name] as number | null | undefined;
    return (
      <td key={`${ref}:${measure.name}`}>
        <span className="sv" data-lens-ref={row ? `${envelope.query}.${measure.name}[${ref}]` : undefined} title={formatExact(number, measure.format)}>
          {row ? formatValue(number, measure.format, { numbers: shown.numbers, decimals: measure.decimals }) : ''}
        </span>
      </td>
    );
  };
  return (
    <div className="tbl pivot" data-lens-pivot={across.name}>
      <table>
        <thead>
          {many ? (
            <tr className="groups">
              <th colSpan={down.length} />
              {members.map((member) => (
                <th className="group" colSpan={measures.length} key={member}>
                  {memberLabel(member)}
                </th>
              ))}
              {totalled ? (
                <th className="group" colSpan={measures.length}>
                  {TOTAL}
                </th>
              ) : null}
            </tr>
          ) : null}
          {groups.length ? (
            <tr className="groups">
              <th colSpan={down.length} />
              {[...members, ...ends].flatMap((member) => spans.map((group, index) => (
                <th className={group.label ? 'group' : undefined} colSpan={group.span} key={`${member}:${index}`}>
                  {group.label}
                </th>
              )))}
            </tr>
          ) : null}
          <tr>
            {down.map((dim) => (
              <th className="dim" key={dim.name}>
                {dim.label}
              </th>
            ))}
            {many
              ? [...members, ...ends].flatMap((member) => measures.map((measure) => <th key={`${member}:${measure.name}`}>{measure.label}</th>))
              : [...members.map((member) => <th key={member}>{memberLabel(member)}</th>), ...ends.map((end) => <th key="total">{end}</th>)]}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row) => {
            const key = keyOf(row);
            return (
              <tr key={key}>
                {down.map((dim) => (
                  <td className="dim" key={dim.name} title={memberLabel(String(row[dim.name] ?? ''))}>
                    {memberLabel(String(row[dim.name] ?? ''))}
                  </td>
                ))}
                {members.flatMap((member) => measures.map((measure) => value(cell.get(`${key}»${member}`), measure, `${key}›${member}`)))}
                {totalled ? measures.map((measure) => value(rowTotals.get(key), measure, key)) : null}
              </tr>
            );
          })}
          {totalled && envelope.totals?.current ? (
            <tr className="total">
              <td colSpan={down.length}>{totalLabel}</td>
              {members.flatMap((member) => measures.map((measure) => <td key={`${member}:${measure.name}`} />))}
              {measures.map((measure) => value(envelope.totals!.current, measure, 'total'))}
            </tr>
          ) : null}
        </tbody>
      </table>
      {allMembers.length > MOST_MEMBERS ? (
        printing ? (
          <p className="bn">{`First ${MOST_MEMBERS} of ${allMembers.length} across columns; Download CSV has them all.`}</p>
        ) : (
          <div aria-label="Across columns" className="pivot-pages">
            <button className="all-rows" disabled={currentAcrossPage === 0} onClick={() => setAcrossPage(currentAcrossPage - 1)} type="button">Previous columns</button>
            <span>{`Across columns ${currentAcrossPage * MOST_MEMBERS + 1}–${Math.min((currentAcrossPage + 1) * MOST_MEMBERS, allMembers.length)} of ${allMembers.length}`}</span>
            <button className="all-rows" disabled={currentAcrossPage === lastAcrossPage} onClick={() => setAcrossPage(currentAcrossPage + 1)} type="button">Next columns</button>
          </div>
        )
      ) : null}
      {!printing && rows.length > ROWS_PER_PAGE ? (
        <div className="pivot-pages">
          <button className="all-rows" disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)} type="button">Previous</button>
          <span>{`${currentPage * ROWS_PER_PAGE + 1}–${Math.min((currentPage + 1) * ROWS_PER_PAGE, rows.length)} of ${rows.length}`}</span>
          <button className="all-rows" disabled={currentPage === lastPage} onClick={() => setPage(currentPage + 1)} type="button">Next</button>
        </div>
      ) : null}
    </div>
  );
}
